const REDACTED = '[REDACTED]';

const EMAIL_REGEX = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const PHONE_REGEX = /(?:\+?\d{1,2}[\s.-]?)?(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}\b/g;
const BEARER_TOKEN_REGEX = /\bBearer\s+[A-Za-z0-9-._~+/]+=*\b/gi;
const TOKEN_KEY_VALUE_REGEX = /([?&#](?:access_?token|refresh_?token|token|authorization|signature|sig|x-amz-signature)=)([^&#\s]+)/gi;
const SIGNED_URL_PARAM_REGEX = /([?&#](?:x-amz-credential|x-amz-security-token|x-amz-signature|x-goog-signature|x-goog-credential|signature|sig)=)([^&#\s]+)/gi;
const JWT_LIKE_REGEX = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9._-]+\.[A-Za-z0-9._-]+\b/g;

const SENSITIVE_KEY_REGEX =
  /(?:^|[_-])(transcript|life[_-]?review(?:[_-]?(?:response|answer))?|message(?:[_-]?text)?|recipient(?:[_-]?name)?|patient(?:[_-]?name)?|file[_-]?name|body|content|answer|response|signed[_-]?url|url)(?:$|[_-])/i;
const SENSITIVE_URL_KEY_REGEX = /(?:^|[_-])(signed[_-]?url|url)(?:$|[_-])/i;

const isProduction = (): boolean => process.env.NODE_ENV === 'production';

const shouldRedactByKey = (key: string): boolean => {
  return SENSITIVE_KEY_REGEX.test(key);
};

const shouldSanitizeUrlByKey = (key: string): boolean => {
  return SENSITIVE_URL_KEY_REGEX.test(key);
};

const sanitizeUrlValue = (value: string): string => {
  const sanitized = sanitizeString(value);
  return sanitized === value ? REDACTED : sanitized;
};

const sanitizeString = (value: string): string => {
  return value
    .replace(EMAIL_REGEX, REDACTED)
    .replace(PHONE_REGEX, REDACTED)
    .replace(BEARER_TOKEN_REGEX, `Bearer ${REDACTED}`)
    .replace(TOKEN_KEY_VALUE_REGEX, `$1${REDACTED}`)
    .replace(SIGNED_URL_PARAM_REGEX, `$1${REDACTED}`)
    .replace(JWT_LIKE_REGEX, REDACTED);
};

export const redactSensitiveData = (value: unknown): unknown => {
  if (typeof value === 'string') {
    return sanitizeString(value);
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: sanitizeString(value.message),
      stack: value.stack ? sanitizeString(value.stack) : undefined
    };
  }

  if (Array.isArray(value)) {
    return value.map((item) => redactSensitiveData(item));
  }

  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [key, item]) => {
      if (shouldRedactByKey(key)) {
        if (typeof item === 'string' && shouldSanitizeUrlByKey(key)) {
          acc[key] = sanitizeUrlValue(item);
          return acc;
        }

        acc[key] = REDACTED;
        return acc;
      }

      acc[key] = redactSensitiveData(item);
      return acc;
    }, {});
  }

  return value;
};

const buildSanitizedArgs = (message: string, args: unknown[]): unknown[] => {
  return [sanitizeString(message), ...args.map((arg) => redactSensitiveData(arg))];
};

export const securityLogger = {
  error: (message: string, ...args: unknown[]) => {
    if (isProduction()) {
      return;
    }

    console.error(...buildSanitizedArgs(message, args));
  },
  warn: (message: string, ...args: unknown[]) => {
    if (isProduction()) {
      return;
    }

    console.warn(...buildSanitizedArgs(message, args));
  },
  info: (message: string, ...args: unknown[]) => {
    if (isProduction()) {
      return;
    }

    console.info(...buildSanitizedArgs(message, args));
  }
};
